import React, { useState, useContext } from "react";
import axios from "axios";
import { Dropdown, Tag, Avatar, Spin, message } from "antd";
import { API_DOMAIN } from "../constants";
import { AuthContext } from "../contexts/AuthContext";
import { _fetchAgents, getAgentColor, agentInitials } from "./helpers";

export function AgentDropdown({ chat, onAssigned, isDark, compact = false }) {
  const { authState } = useContext(AuthContext);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const onOpenChange = open => {
    if (!open) return;
    setAgentsLoading(true);
    _fetchAgents().then(list => { setAgents(list); setAgentsLoading(false); });
  };

  const doAssign = async (agent) => {
    const ticket = chat?.raw?.ticket;
    if (!ticket || assigning) return;
    setAssigning(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/wati_messages/assign_chat_to_agent",
        {
          ticketId: ticket.id,
          agentId: agent.id,
          userId: authState.id,
        },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data?.success) throw new Error(res.data?.message);
      onAssigned?.({ agent: agent.fullName });
      message.success(`Assigned to ${agent.firstName}`);
    } catch (e) {
      message.error(e.message || "Failed to assign agent");
    } finally {
      setAssigning(false);
    }
  };

  const _makeAgentItem = (a) => ({
    key: a.id,
    label: (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", minWidth: 190 }}>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <Avatar size={28} style={{
            backgroundColor: getAgentColor(a.fullName), fontSize: 11, fontWeight: 700,
            ...(isDark ? { outline: "2px solid rgba(255,255,255,0.18)", outlineOffset: 1 } : {}),
          }}>
            {agentInitials(a.fullName)}
          </Avatar>
          <span style={{ position: "absolute", bottom: 0, right: -1, width: 8, height: 8, borderRadius: "50%", background: (a.online || a.isBot) ? "#52c41a" : "#bfbfbf", border: `1.5px solid ${isDark ? "#1f2c34" : "#fff"}` }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, lineHeight: "1.3", color: isDark ? "#e9edef" : undefined }}>{a.fullName}</div>
          {a.email && <div style={{ fontSize: 10, color: isDark ? "#8696a0" : "#8c8c8c", overflow: "hidden", textOverflow: "ellipsis" }}>{a.email}</div>}
        </div>
      </div>
    ),
  });

  const _sectionLabel = (text) => {
    const isOnline = text === "ONLINE";
    const lineColor = isOnline ? (isDark ? "#274d2e" : "#b7eb8f") : (isDark ? "#2a3942" : "#f0f0f0");
    const textColor = isOnline ? "#52c41a" : (isDark ? "#8696a0" : "#bfbfbf");
    return {
      key: `__sec_${text}`,
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 4px" }}>
          <div style={{ flex: 1, height: 1, background: lineColor }} />
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", color: textColor, whiteSpace: "nowrap" }}>{text}</span>
          <div style={{ flex: 1, height: 1, background: lineColor }} />
        </div>
      ),
      disabled: true,
    };
  };

  const _onlineAgents  = agents.filter(a =>  (a.online || a.isBot));
  const _offlineAgents = agents.filter(a => !(a.online || a.isBot));

  const menuItems = agentsLoading
    ? [{ key: "__loading", label: <div style={{ padding: "6px 0", textAlign: "center" }}><Spin size="small" /></div>, disabled: true }]
    : [
      ...(_onlineAgents.length > 0 ? [_sectionLabel("ONLINE")] : []),
      ..._onlineAgents.map(_makeAgentItem),
      ...(_offlineAgents.length > 0 ? [_sectionLabel("OFFLINE")] : []),
      ..._offlineAgents.map(_makeAgentItem),
    ];

  const agentColor = getAgentColor(chat?.agent || "");

  return (
    <Dropdown
      trigger={["click"]}
      onOpenChange={onOpenChange}
      disabled={assigning}
      placement="bottomLeft"
      dropdownRender={menu => (
        <div style={{ maxHeight: 280, overflowY: "auto", borderRadius: 8, boxShadow: `0 6px 16px rgba(0,0,0,${isDark ? 0.5 : 0.14})` }}>
          {isDark
            ? React.cloneElement(menu, {
                className: `${menu.props?.className || ""} qv4-dark-menu`.trim(),
                style: { ...(menu.props?.style || {}), background: "#1f2c34", border: "1px solid #2a3942" },
              })
            : menu}
        </div>
      )}
      menu={{
        items: menuItems,
        onClick: ({ key }) => { const a = agents.find(x => x.id === key); if (a) doAssign(a); },
      }}
    >
      <Tag onClick={e => e.stopPropagation()} style={{
        fontSize: 10, margin: 0, cursor: assigning ? "wait" : "pointer",
        padding: compact ? "0 5px" : "0 6px",
        border: `1.5px solid ${agentColor}`, color: agentColor, background: "transparent",
        display: "inline-flex", alignItems: "center", gap: 3,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        ...(compact ? { maxWidth: 110, flexShrink: 0 } : { maxWidth: "100%" }),
        boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
      }}>
        {assigning
          ? <><Spin size="small" /><span style={{ marginLeft: 4 }}>Assigning…</span></>
          : <>
            <Avatar size={compact ? 11 : 13} style={{ backgroundColor: agentColor, fontSize: compact ? 7 : 8, flexShrink: 0 }}>
              {agentInitials(chat?.agent || "?")}
            </Avatar>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{chat?.agent || "Assign"}</span>
            <span style={{ fontSize: 8, opacity: 0.6 }}>▾</span>
          </>
        }
      </Tag>
    </Dropdown>
  );
}
